<template>
    <section class="w-full sm:pb-6 flex flex-col">
        <!-- Header with title and actions -->
        <header
            class="flex justify-between items-start sm:items-center sm:mb-8 mb-5 flex-col sm:flex-row"
        >
            <div class="flex items-center gap-3">
                <h1 class="text-2xl font-semibold text-primary">My Wallet</h1>
                <!-- MY1-2830: subtle background-refresh indicator beside the title.
                     The relative wrapper confines LoadingState's absolute-inset
                     spinner to this box; scale-[0.8] renders it 20% smaller. Move
                     this single block to reposition the loader. -->
                <div
                    v-if="refreshing"
                    class="relative w-5 h-5 scale-[0.45] origin-center"
                    role="status"
                    aria-live="polite"
                    title="Refreshing your wallet…"
                >
                    <LoadingState
                        :loading="true"
                        size="small"
                        :noBackground="true"
                    />
                </div>
            </div>

            <div class="flex gap-2 items-center">
                <button
                    class="btn-base btn-10x-orange !h-10"
                    id="hs-wallet-add-account"
                    @click="$emit('addAccount')"
                >
                    <Icon icon="addCard" class="w-5 h-5" />
                    <span class="text-sm font-medium">Add card</span>
                </button>
                <ButtonPopover buttonClass="btn-base btn-white !h-10">
                    <template #button-content>
                        <Icon
                            icon="dots-vertical"
                            class="w-5 h-5 icons-primary"
                        />
                    </template>
                    <template #default="{ closePopover }">
                        <ul class="text-slate-800 dark:text-slate-100 m-0 p-0">
                            <li class="flex items-center">
                                <button
                                    type="button"
                                    class="btn-base btn-popover"
                                    @click="handleAddUser()"
                                >
                                    <Icon icon="addUser" class="w-5 h-5" />

                                    Add user
                                </button>
                            </li>
                            <li class="flex items-center">
                                <button
                                    type="button"
                                    class="btn-base btn-popover"
                                    @click="
                                        walletStore.setShowBusinessAddEditModal(
                                            true
                                        )
                                    "
                                >
                                    <Icon icon="addEntity" class="w-5 h-5" />

                                    Add business entity
                                </button>
                            </li>
                            <li class="flex items-center">
                                <Link
                                    href="/wallet/members"
                                    class="btn-base btn-popover"
                                    id="hs-wallet-manage-connections"
                                >
                                    <Icon
                                        icon="editConnection"
                                        class="w-5 h-5"
                                    />

                                    Manage connections
                                </Link>
                            </li>
                            <li class="flex items-center">
                                <button
                                    type="button"
                                    class="btn-base btn-popover"
                                    @click="
                                        walletStore.setShowManageBusinessesModal(
                                            true
                                        )
                                    "
                                >
                                    <Icon icon="editEntity" class="w-5 h-5" />

                                    Manage business entities
                                </button>
                            </li>
                        </ul>
                    </template>
                </ButtonPopover>
            </div>
        </header>

        <!-- Filters section -->
        <div class="flex justify-between items-center">
            <!-- Filter buttons -->
            <div
                class="flex items-center gap-4"
                v-if="walletUsersAndEntities.length > 0"
            >
                <ButtonPopover
                    class="sm:inline hidden"
                    buttonClass="btn-base btn-white !pl-4 !pr-3 h-10"
                >
                    <template #button-content>
                        <span class="text-primary text-sm font-medium">
                            User
                        </span>
                        <Transition name="fade">
                            <span
                                v-if="selectedUsersOrEntities.length >= 1"
                                class="w-5 h-5 text-white rounded-full inline-flex items-center justify-center bg-slate-800 text-xs font-medium"
                            >
                                {{ selectedUsersOrEntities.length }}
                            </span>
                        </Transition>
                        <Icon
                            icon="chevron-down-small"
                            class="w-5 h-5 icons-primary"
                        />
                    </template>
                    <template #default="{ closePopover }">
                        <ul>
                            <li
                                v-for="item in walletUsersAndEntities"
                                :key="item.id"
                            >
                                <button
                                    type="button"
                                    class="w-full p-2 text-sm text-gray-900 dark:text-gray-100 rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 dark:bg-slate-800 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none transition-colors flex items-center gap-2"
                                    @click="selectUser(item, closePopover)"
                                >
                                    <input
                                        type="checkbox"
                                        class="rounded text-10x-orange"
                                        :checked="isUserSelected(item)"
                                        @click.stop
                                        @change="toggleUserSelection(item)"
                                    />
                                    <span>{{ item.name }}</span>
                                </button>
                            </li>
                            <li v-if="selectedUsersOrEntities.length >= 1">
                                <button
                                    type="button"
                                    class="w-full p-2 text-sm text-gray-900 dark:text-gray-100 rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 dark:bg-slate-800 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none transition-colors flex items-center gap-2"
                                    @click="
                                        walletStore.resetSelectedUserOrEntityFilter()
                                    "
                                >
                                    <span>Reset</span>
                                </button>
                            </li>
                        </ul>
                    </template>
                </ButtonPopover>
                <!-- Bank filter -->
                <ButtonPopover
                    v-if="walletBanks.length > 0"
                    class="sm:inline hidden"
                    buttonClass="btn-base btn-white !pl-4 !pr-3 h-10"
                >
                    <template #button-content>
                        <span class="text-primary text-sm font-medium">
                            Bank
                        </span>
                        <Transition name="fade">
                            <span
                                v-if="selectedBanks.length >= 1"
                                class="w-5 h-5 text-white rounded-full inline-flex items-center justify-center bg-slate-800 text-xs font-medium"
                            >
                                {{ selectedBanks.length }}
                            </span>
                        </Transition>
                        <Icon
                            icon="chevron-down-small"
                            class="w-5 h-5 icons-primary"
                        />
                    </template>
                    <template #default="{ closePopover }">
                        <ul>
                            <li v-for="bank in walletBanks" :key="bank.id">
                                <button
                                    type="button"
                                    class="w-full p-2 text-sm text-gray-900 dark:text-gray-100 rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 dark:bg-slate-800 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none transition-colors flex items-center gap-2"
                                    @click="selectBank(bank, closePopover)"
                                >
                                    <input
                                        type="checkbox"
                                        class="rounded text-10x-orange"
                                        :checked="isBankSelected(bank)"
                                        @click.stop
                                        @change="toggleBankSelection(bank)"
                                    />
                                    <span>{{ bank.name }}</span>
                                </button>
                            </li>
                            <li v-if="selectedBanks.length >= 1">
                                <button
                                    type="button"
                                    class="w-full p-2 text-sm text-gray-900 dark:text-gray-100 rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 dark:bg-slate-800 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none transition-colors flex items-center gap-2"
                                    @click="
                                        walletStore.resetSelectedBankFilter()
                                    "
                                >
                                    <span>Reset</span>
                                </button>
                            </li>
                        </ul>
                    </template>
                </ButtonPopover>
                <!-- Card type filter -->
                <ButtonPopover
                    v-if="cardTypes.length > 0"
                    class="sm:inline hidden"
                    buttonClass="btn-base btn-white !pl-4 !pr-3 h-10"
                >
                    <template #button-content>
                        <span class="text-primary text-sm font-medium">
                            Card Type
                        </span>
                        <Transition name="fade">
                            <span
                                v-if="selectedCardTypes.length >= 1"
                                class="w-5 h-5 text-white rounded-full inline-flex items-center justify-center bg-slate-800 text-xs font-medium"
                            >
                                {{ selectedCardTypes.length }}
                            </span>
                        </Transition>
                        <Icon
                            icon="chevron-down-small"
                            class="w-5 h-5 icons-primary"
                        />
                    </template>
                    <template #default="{ closePopover }">
                        <ul>
                            <li v-for="type in cardTypes" :key="type.id">
                                <button
                                    type="button"
                                    class="w-full p-2 text-sm text-gray-900 dark:text-gray-100 rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 dark:bg-slate-800 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none transition-colors flex items-center gap-2"
                                    @click="selectCardType(type, closePopover)"
                                >
                                    <input
                                        type="checkbox"
                                        class="rounded text-10x-orange"
                                        :checked="isCardTypeSelected(type)"
                                        @click.stop
                                        @change="toggleCardTypeSelection(type)"
                                    />
                                    <span>
                                        {{ $filters.titleCase(type.name) }}
                                    </span>
                                </button>
                            </li>
                            <li v-if="selectedCardTypes.length >= 1">
                                <button
                                    type="button"
                                    class="w-full p-2 text-sm text-gray-900 dark:text-gray-100 rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 dark:bg-slate-800 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none transition-colors flex items-center gap-2"
                                    @click="
                                        walletStore.resetSelectedCardTypeFilter()
                                    "
                                >
                                    <span>Reset</span>
                                </button>
                            </li>
                        </ul>
                    </template>
                </ButtonPopover>
                <!-- Status filter -->
                <ButtonPopover
                    class="sm:inline hidden"
                    buttonClass="btn-base btn-white !pl-4 !pr-3 h-10"
                >
                    <template #button-content>
                        <span class="text-primary text-sm font-medium">
                            Status
                        </span>
                        <Transition name="fade">
                            <span
                                v-if="selectedStatuses.length >= 1"
                                class="w-5 h-5 text-white rounded-full inline-flex items-center justify-center bg-slate-800 text-xs font-medium"
                            >
                                {{ selectedStatuses.length }}
                            </span>
                        </Transition>
                        <Icon
                            icon="chevron-down-small"
                            class="w-5 h-5 icons-primary"
                        />
                    </template>
                    <template #default="{ closePopover }">
                        <ul>
                            <li v-for="status in statuses" :key="status.id">
                                <button
                                    type="button"
                                    class="w-full p-2 text-sm text-gray-900 dark:text-gray-100 rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 dark:bg-slate-800 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none transition-colors flex items-center gap-2"
                                    @click="selectStatus(status, closePopover)"
                                >
                                    <input
                                        type="checkbox"
                                        class="rounded text-10x-orange"
                                        :checked="isStatusSelected(status)"
                                        @click.stop
                                        @change="toggleStatusSelection(status)"
                                    />
                                    <span>{{ status.name }}</span>
                                </button>
                            </li>
                            <li v-if="selectedStatuses.length >= 1">
                                <button
                                    type="button"
                                    class="w-full p-2 text-sm text-gray-900 dark:text-gray-100 rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 dark:bg-slate-800 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none transition-colors flex items-center gap-2"
                                    @click="
                                        walletStore.resetSelectedStatusFilter()
                                    "
                                >
                                    <span>Reset</span>
                                </button>
                            </li>
                        </ul>
                    </template>
                </ButtonPopover>
                <!-- All filters button -->
                <button
                    class="btn-base btn-white !pl-4 !pr-3 h-10"
                    @click="openFiltersModal"
                >
                    <Icon icon="filters" class="w-5 h-5 icons-primary" />
                    <span
                        class="text-primary text-sm font-medium inline sm:hidden"
                    >
                        Filters
                    </span>
                    <span
                        class="text-primary text-sm font-medium sm:inline hidden"
                    >
                        All filters
                    </span>
                    <Transition name="fade">
                        <span
                            v-if="activeFiltersCount > 0"
                            class="w-5 h-5 text-white rounded-full inline-flex items-center justify-center bg-slate-800 text-xs font-medium"
                        >
                            {{ activeFiltersCount }}
                        </span>
                    </Transition>
                </button>

                <!-- Clear filters button - only shown when filters are active -->
                <button
                    v-if="hasActiveFilters"
                    class="btn-base btn-transparent !pl-4 !pr-3 !py-1.5"
                    @click="clearAllFilters"
                >
                    <span class="text-primary text-sm font-medium">
                        Reset all
                    </span>
                    <Icon icon="x" class="w-3 h-3 icons-primary" />
                </button>
            </div>
            <div class="flex gap-2">
                <!-- mobile-only buttons: -->
                <button
                    class="btn-base btn-white sm:!hidden !p-2"
                    @click="() => (showSortModal = true)"
                >
                    <Icon icon="sort" class="w-5 h-5 icons-primary" />
                    <span class="sr-only">Sort</span>
                </button>
                <button
                    class="btn-base btn-white sm:!hidden !p-2"
                    @click="() => (showDisplayModal = true)"
                >
                    <Icon icon="settings" class="w-5 h-5 icons-primary" />
                    <span class="sr-only">Display settings</span>
                </button>
                <!-- Display options, hidden on mobile: -->
                <ButtonPopover
                    buttonClass="btn-base btn-transparent !sm:pl-4 !sm:pr-3 !py-1.5 h-10"
                    class="hidden sm:block"
                >
                    <template #button-content>
                        <Icon icon="settings" class="w-5 h-5 icons-primary" />
                        <span
                            class="text-primary text-sm font-medium sm:inline hidden"
                        >
                            Display
                        </span>
                        <Icon
                            icon="chevron-down-small"
                            class="w-5 h-5 icons-primary sm:inline hidden"
                        />
                    </template>
                    <template #default="{ closeDisplayOptionsPopover }">
                        <section class="max-w-64 flex flex-col">
                            <label
                                class="flex items-center justify-between gap-2 p-2"
                            >
                                <div class="flex items-center gap-2">
                                    <Icon
                                        icon="groups"
                                        class="w-4 h-4 text-gray-700 dark:text-gray-200"
                                        containerClass="justify-start flex items-center"
                                    />
                                    <span class="text-gray-700 dark:text-gray-200 text-sm">
                                        Group by
                                    </span>
                                </div>
                                <ButtonPopover
                                    buttonClass="!p-0 !justify-end btn-base btn-transparent !font-normal"
                                    popoverClass="bg-white dark:bg-slate-800 rounded-lg shadow-lg min-w-40 z-40 outline outline-1 outline-offset-[-1px] outline-slate-200 p-2"
                                >
                                    <template #button-content>
                                        <div
                                            class="flex items-center gap-1 pl-1"
                                        >
                                            <span
                                                class="text-gray-400 text-sm font-normal"
                                            >
                                                {{
                                                    groupBy?.id
                                                        ? groupBy.name
                                                        : 'Not selected'
                                                }}
                                            </span>
                                            <Icon
                                                icon="chevron-down-small"
                                                class="w-5 h-5 text-slate-700"
                                            />
                                        </div>
                                    </template>
                                    <template #default="{ closePopover }">
                                        <div class="flex flex-col gap-1">
                                            <button
                                                class="self-stretch bg-white dark:bg-slate-800 inline-flex justify-start items-start flex-1 p-2 rounded text-slate-700 text-sm font-normal hover:bg-slate-100 dark:bg-slate-700 focus:bg-slate-100 dark:bg-slate-700 focus:outline-none"
                                                @click="
                                                    walletStore.setGroupBy(
                                                        null
                                                    );
                                                    closePopover();
                                                "
                                            >
                                                None selected
                                            </button>
                                            <button
                                                v-for="option in groupingOptions"
                                                :key="option.id"
                                                class="self-stretch bg-white dark:bg-slate-800 inline-flex justify-start items-start flex-1 p-2 rounded text-slate-700 text-sm font-normal hover:bg-slate-100 dark:bg-slate-700 focus:bg-slate-100 dark:bg-slate-700 focus:outline-none"
                                                :class="{
                                                    'bg-slate-100 dark:bg-slate-700':
                                                        groupBy?.id ===
                                                        option.id,
                                                }"
                                                @click="
                                                    walletStore.setGroupBy(
                                                        option
                                                    );
                                                    closePopover();
                                                "
                                            >
                                                {{ option.name }}
                                            </button>
                                        </div>
                                    </template>
                                </ButtonPopover>
                            </label>
                            <label
                                v-if="groupBy?.id"
                                class="flex items-center justify-between gap-2 p-2"
                            >
                                <div class="flex items-center gap-2">
                                    <Icon
                                        icon="subgroup"
                                        class="w-4 h-4 text-gray-700 dark:text-gray-200"
                                        containerClass="justify-start flex items-center"
                                    />
                                    <span class="text-gray-700 dark:text-gray-200 text-sm">
                                        Subgrouping
                                    </span>
                                </div>
                                <ButtonPopover
                                    buttonClass="!p-0 !justify-end btn-base btn-transparent !font-normal"
                                    popoverClass="bg-white dark:bg-slate-800 rounded-lg shadow-lg min-w-40 z-30 outline outline-1 outline-offset-[-1px] outline-slate-200 p-2"
                                >
                                    <template #button-content>
                                        <div
                                            class="flex items-center gap-1 pl-1"
                                        >
                                            <span
                                                class="text-slate-400 text-sm !font-normal"
                                            >
                                                {{
                                                    subgroupBy?.id
                                                        ? subgroupBy.name
                                                        : 'Not selected'
                                                }}
                                            </span>
                                            <Icon
                                                icon="chevron-down-small"
                                                class="w-5 h-5 text-slate-700"
                                            />
                                        </div>
                                    </template>
                                    <template #default="{ closePopover }">
                                        <div class="flex flex-col gap-1">
                                            <button
                                                class="self-stretch bg-white dark:bg-slate-800 inline-flex justify-start items-start flex-1 p-2 rounded text-slate-700 text-sm font-normal hover:bg-slate-100 dark:bg-slate-700 focus:bg-slate-100 dark:bg-slate-700 focus:outline-none"
                                                @click="
                                                    walletStore.setSubgroupBy(
                                                        null
                                                    );
                                                    closePopover();
                                                "
                                            >
                                                None selected
                                            </button>
                                            <button
                                                v-for="option in subGroupingOptions"
                                                :key="option"
                                                class="self-stretch bg-white dark:bg-slate-800 inline-flex justify-start items-start flex-1 p-2 rounded text-slate-700 text-sm font-normal hover:bg-slate-100 dark:bg-slate-700 focus:bg-slate-100 dark:bg-slate-700 focus:outline-none"
                                                :class="{
                                                    'bg-slate-100 dark:bg-slate-700':
                                                        subgroupBy?.id ===
                                                        option.id,
                                                }"
                                                @click="
                                                    walletStore.setSubgroupBy(
                                                        option
                                                    );
                                                    closePopover();
                                                "
                                            >
                                                {{ option.name }}
                                            </button>
                                        </div>
                                    </template>
                                </ButtonPopover>
                            </label>
                            <label
                                class="w-full h-9 p-2 rounded flex justify-between items-center"
                            >
                                <div class="flex items-center gap-2">
                                    <Icon
                                        icon="columns"
                                        class="w-4 h-4 text-slate-700"
                                        containerClass="justify-start flex items-center"
                                    />
                                    <span class="text-slate-700 text-sm">
                                        Columns
                                    </span>
                                </div>
                                <ButtonPopover
                                    buttonClass="!p-0 !justify-end btn-base btn-transparent !font-normal"
                                    :closeOnSelect="false"
                                >
                                    <template #button-content>
                                        <div
                                            class="flex items-center gap-1 pl-1"
                                        >
                                            <span
                                                class="text-slate-400 text-sm !font-normal"
                                            >
                                                {{ visibleColumnsCount }}
                                                shown
                                            </span>
                                            <Icon
                                                icon="chevron-down-small"
                                                class="w-5 h-5 text-slate-700"
                                            />
                                        </div>
                                    </template>
                                    <template #default="{ closePopover }">
                                        <div class="flex flex-col gap-1">
                                            <div
                                                v-for="col in columnOptions"
                                                :key="col.id"
                                                class="self-stretch bg-white dark:bg-slate-800 inline-flex justify-between items-center flex-1 p-2 rounded text-slate-700 text-sm font-normal hover:bg-slate-100 dark:bg-slate-700 focus:bg-slate-100 dark:bg-slate-700 focus:outline-none cursor-pointer"
                                                :class="{
                                                    'bg-slate-100 dark:bg-slate-700':
                                                        visibleColumns.includes(
                                                            col.id
                                                        ),
                                                }"
                                                @click.stop="
                                                    walletStore.toggleVisibleColumn(
                                                        col.id
                                                    )
                                                "
                                            >
                                                {{ col.name }}
                                                <input
                                                    type="checkbox"
                                                    class="sr-only"
                                                    :checked="
                                                        visibleColumns.includes(
                                                            col.id
                                                        )
                                                    "
                                                />
                                                <Transition name="fade">
                                                    <Icon
                                                        v-if="
                                                            visibleColumns.includes(
                                                                col.id
                                                            )
                                                        "
                                                        icon="checkmark"
                                                        class="w-5 h-5"
                                                    />
                                                </Transition>
                                            </div>
                                            <!-- Reset Order Button -->
                                            <div
                                                class="border-t border-gray-200 dark:border-slate-700 mt-2 pt-2"
                                            >
                                                <button
                                                    class="w-full text-left p-2 text-slate-600 dark:text-slate-300 text-sm font-normal hover:bg-slate-50 dark:hover:bg-slate-700 focus:bg-slate-50 focus:outline-none cursor-pointer rounded flex items-center gap-2"
                                                    @click.stop="
                                                        walletStore.resetColumnOrder()
                                                    "
                                                >
                                                    <Icon
                                                        icon="refresh"
                                                        class="w-4 h-4"
                                                    />
                                                    Reset order
                                                </button>
                                            </div>
                                        </div>
                                    </template>
                                </ButtonPopover>
                            </label>

                            <div class="w-full p-2">
                                <hr class="border-t border-gray-200 dark:border-slate-700" />
                            </div>

                            <button
                                class="btn-base btn-transparent !justify-between !pl-2"
                            >
                                <span class="text-gray-700 dark:text-gray-200 text-sm font-normal">
                                    Show cards #s
                                </span>
                                <Switch
                                    :value="showLastFour"
                                    @input="walletStore.setShowLastFour($event)"
                                    label="Show cards numbers"
                                />
                            </button>

                            <button
                                class="btn-base btn-transparent !justify-between !pl-2"
                            >
                                <span class="text-gray-700 dark:text-gray-200 text-sm font-normal">
                                    Group same cards
                                </span>
                                <Switch
                                    :value="groupSameCards"
                                    @input="
                                        walletStore.setGroupSameCards($event)
                                    "
                                    label="Group same cards"
                                />
                            </button>

                            <button
                                class="btn-base btn-transparent !justify-between !pl-2"
                            >
                                <span class="text-gray-700 dark:text-gray-200 text-sm font-normal">
                                    Show hidden cards
                                </span>
                                <Switch
                                    :value="showHiddenCards"
                                    @input="
                                        walletStore.setShowHiddenCards($event)
                                    "
                                    label="Show hidden cards"
                                />
                            </button>
                        </section>
                    </template>
                </ButtonPopover>
            </div>
        </div>
    </section>
    <WalletFiltersModal
        :isOpen="filtersModalIsOpen"
        @close="closeFiltersModal"
    />
    <WalletTableDisplayModal
        :isOpen="showDisplayModal"
        @close="() => (showDisplayModal = false)"
    />
    <WalletTableSortModal
        :isOpen="showSortModal"
        @close="() => (showSortModal = false)"
    />
</template>

<script setup>
import { Link } from '@inertiajs/vue3';
import { ref, computed, onMounted, onUnmounted } from 'vue';
import Icon from '@/components/ui/icon/icon.vue';
import ButtonPopover from '@/components/ui/button-popover.vue';
import Switch from '@/components/ui/switch/switch.vue';
import LoadingState from '@/components/ui/loading-state/loading-state.vue';
import WalletFiltersModal from '@/components/wallet/modals/wallet-filters-modal.vue';
import WalletTableDisplayModal from '@/components/wallet/wallet-table-display-modal.vue';
import WalletTableSortModal from '@/components/wallet/partials/wallet-table-sort-modal.vue';
import { useWalletStore } from '@/store/wallet';
import { storeToRefs } from 'pinia';

const emit = defineEmits(['addAccount']);

// MY1-2830: when true, a subtle spinner shows in the header (a background refresh
// over already-rendered wallet data). The full-page spinner is reserved for cold loads.
defineProps({
    refreshing: {
        type: Boolean,
        default: false,
    },
});

const walletStore = useWalletStore();
const {
    columns,
    orderedColumns,
    groupBy,
    groupSameCards,
    groupingOptions,
    hasActiveFilters,
    activeFiltersCount,
    showHiddenCards,
    showLastFour,
    walletUsersAndEntities,
    walletBanks,
    cardTypes,
    statuses,
    subgroupBy,
    subGroupingOptions,
    sortBy,
    sortDirection,
    selectedUsersOrEntities,
    selectedBanks,
    selectedCardTypes,
    selectedStatuses,
    visibleColumns,
} = storeToRefs(walletStore);

const {
    setShowAddUserModal,
    setShowAddEntityModal,
    setShowManageEntitiesModal,
    setShowBusinessAddEditModal,
    setSortBy,
} = walletStore;

const showSortModal = ref(false);
const showDisplayModal = ref(false);

const filtersModalIsOpen = ref(false);

const columnOptions = computed(() => {
    return orderedColumns.value.filter(
        (c) => c.id !== 'actions' && c.id !== 'title'
    );
});

const visibleColumnsCount = computed(() => {
    return visibleColumns.value.length - 1;
});

const isUserSelected = (user) => {
    return selectedUsersOrEntities.value.some((u) => u.id === user.id);
};

const isBankSelected = (bank) => {
    return selectedBanks.value.some((b) => b.id === bank.id);
};

const isCardTypeSelected = (type) => {
    return selectedCardTypes.value.some((t) => t.id === type.id);
};

const isStatusSelected = (status) => {
    return selectedStatuses.value.some((s) => s.id === status.id);
};

const selectUser = (user, closePopover) => {
    walletStore.toggleUserSelection(user);
};

const selectBank = (bank, closePopover) => {
    walletStore.toggleBankSelection(bank);
};

const selectCardType = (type, closePopover) => {
    walletStore.toggleCardTypeSelection(type);
};

const selectStatus = (status, closePopover) => {
    walletStore.toggleStatusSelection(status);
};

const toggleUserSelection = (user) => {
    walletStore.toggleUserSelection(user);
};

const toggleBankSelection = (bank) => {
    walletStore.toggleBankSelection(bank);
};

const toggleCardTypeSelection = (type) => {
    walletStore.toggleCardTypeSelection(type);
};

const toggleStatusSelection = (status) => {
    walletStore.toggleStatusSelection(status);
};

const openFiltersModal = () => {
    filtersModalIsOpen.value = true;
};

const closeFiltersModal = () => {
    filtersModalIsOpen.value = false;
};

const clearAllFilters = () => {
    walletStore.clearAllFilters();
};

const handleAddUser = () => {
    setShowAddUserModal(true);
};

const handleAddEntity = () => {
    setShowAddEntityModal(true);
};

const handleManageEntities = (closePopover) => {
    setShowManageEntitiesModal(true);
};
</script>
